import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { notificationEndpoints, settingsEndpoints } from '@/lib/api/endpoints';
import { notificationKeys, settingsKeys } from '@/lib/queryKeys';
import {
  useNotificationActions,
  useNotificationStore,
  useNotificationStreamConnected,
} from '@/stores/notification.store';
import type {
  CreateNotificationBroadcastDto,
  CreateNotificationBroadcastResponse,
  NotificationCategory,
  NotificationGroupItemsResponse,
  NotificationPreferencesResponse,
  OperationalNotificationRules,
  NotificationPriority,
  NotificationStatusFilter,
  NotificationUnreadCount,
  NotificationsListResponse,
  StaffNotification,
  UpdateNotificationPreferencesDto,
  UpdateOperationalNotificationRulesDto,
} from '@/types/notifications';

type NotificationsListCache = InfiniteData<NotificationsListResponse, string | null>;
type NotificationListSnapshot = Array<[QueryKey, NotificationsListCache | undefined]>;
type NotificationListFilters = {
  unreadOnly?: boolean;
  category?: NotificationCategory | 'all';
  priority?: NotificationPriority | 'all';
  eventType?: string;
  excludeEventType?: string;
  status?: NotificationStatusFilter;
  limit?: number;
};

export function useNotificationUnreadQuery(options: { enabled?: boolean } = {}) {
  const { setUnreadCount } = useNotificationActions();
  const streamConnected = useNotificationStreamConnected();
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const result = await apiGet<NotificationUnreadCount>(
        operationsClient,
        notificationEndpoints.unreadCount(),
      );
      setUnreadCount(result.count);
      return result;
    },
    enabled: options.enabled ?? true,
    refetchInterval: streamConnected ? false : 60_000,
  });
}

export function useNotificationsList(
  options: {
    unreadOnly?: boolean;
    category?: 'all' | NotificationCategory;
    priority?: 'all' | NotificationPriority;
    eventType?: string;
    excludeEventType?: string;
    status?: NotificationStatusFilter;
    enabled?: boolean;
    limit?: number;
  } = {},
) {
  const limit = options.limit ?? 50;
  const category = options.category && options.category !== 'all' ? options.category : undefined;
  const priority = options.priority && options.priority !== 'all' ? options.priority : undefined;
  const status = options.status;
  return useInfiniteQuery({
    queryKey: notificationKeys.list({
      unreadOnly: options.unreadOnly ?? false,
      category,
      priority,
      eventType: options.eventType,
      excludeEventType: options.excludeEventType,
      status,
      limit,
    }),
    queryFn: ({ pageParam }) =>
      apiGet<NotificationsListResponse>(operationsClient, notificationEndpoints.list(), {
        params: {
          unreadOnly: options.unreadOnly || undefined,
          category: category ? category.toUpperCase() : undefined,
          priority: priority ? priority.toUpperCase() : undefined,
          eventType: options.eventType,
          excludeEventType: options.excludeEventType,
          status,
          limit,
          cursor: typeof pageParam === 'string' ? pageParam : undefined,
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: options.enabled ?? true,
  });
}

export function useNotificationGroupItems(
  recipientId: string | null,
  options: {
    enabled?: boolean;
    limit?: number;
    status?: NotificationStatusFilter;
    firstSeenAt?: string;
    lastEventAt?: string;
  } = {},
) {
  const limit = options.limit ?? 50;
  const status = options.status;
  return useInfiniteQuery({
    queryKey: notificationKeys.groupItems(
      recipientId ?? 'none',
      status,
      options.firstSeenAt,
      options.lastEventAt,
    ),
    queryFn: ({ pageParam }) =>
      apiGet<NotificationGroupItemsResponse>(
        operationsClient,
        notificationEndpoints.groupItems(recipientId ?? ''),
        {
          params: {
            limit,
            status,
            firstSeenAt: options.firstSeenAt,
            lastEventAt: options.lastEventAt,
            cursor: typeof pageParam === 'string' ? pageParam : undefined,
          },
        },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(recipientId) && (options.enabled ?? true),
  });
}

export function useArchiveNotification() {
  const qc = useQueryClient();
  const { setUnreadCount } = useNotificationActions();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<StaffNotification, Record<string, never>>(
        operationsClient,
        notificationEndpoints.archive(id),
        {},
      ),
    onMutate: async (id) => {
      await cancelNotificationQueries(qc);
      const snapshots = getNotificationListSnapshots(qc);
      const previousUnreadCount = useNotificationStore.getState().unreadCount;
      const archivedAt = new Date().toISOString();
      const changedUnread = new Set<string>();

      patchNotificationLists(qc, (item, filters) => {
        if (!matchesNotificationId(item, id)) return item;
        const next: StaffNotification = {
          ...item,
          archivedAt,
          readAt: item.readAt ?? archivedAt,
          seenAt: item.seenAt ?? archivedAt,
        };
        if (item.readAt === null && item.archivedAt === null) {
          changedUnread.add(item.recipientId);
        }
        return matchesListFilters(next, filters) ? next : null;
      });

      if (changedUnread.size > 0) {
        setUnreadCount(previousUnreadCount - changedUnread.size);
      }
      return { snapshots, previousUnreadCount };
    },
    onError: (_error, _id, context) => {
      if (context) {
        restoreNotificationListSnapshots(qc, context.snapshots);
        setUnreadCount(context.previousUnreadCount);
      }
      toast.error("Couldn't archive notification");
    },
    onSettled: async () => {
      await invalidateNotificationQueries(qc);
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  const { setUnreadCount } = useNotificationActions();
  return useMutation({
    mutationFn: (id: string) =>
      apiPatch<StaffNotification, Record<string, never>>(
        operationsClient,
        notificationEndpoints.markRead(id),
        {},
      ),
    onMutate: async (id) => {
      await cancelNotificationQueries(qc);
      const snapshots = getNotificationListSnapshots(qc);
      const previousUnreadCount = useNotificationStore.getState().unreadCount;
      const readAt = new Date().toISOString();
      const changedUnread = new Set<string>();

      patchNotificationLists(qc, (item, filters) => {
        if (!matchesNotificationId(item, id) || item.readAt !== null) return item;
        const next: StaffNotification = {
          ...item,
          readAt,
          seenAt: item.seenAt ?? readAt,
        };
        if (item.archivedAt === null) changedUnread.add(item.recipientId);
        return matchesListFilters(next, filters) ? next : null;
      });

      if (changedUnread.size > 0) {
        setUnreadCount(previousUnreadCount - changedUnread.size);
      }
      return { snapshots, previousUnreadCount };
    },
    onError: (_error, _id, context) => {
      if (context) {
        restoreNotificationListSnapshots(qc, context.snapshots);
        setUnreadCount(context.previousUnreadCount);
      }
      toast.error("Couldn't mark notification as read");
    },
    onSettled: async () => {
      await invalidateNotificationQueries(qc);
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  const { setUnreadCount } = useNotificationActions();
  return useMutation({
    mutationFn: () =>
      apiPost<{ updated: number; count: number }, Record<string, never>>(
        operationsClient,
        notificationEndpoints.readAll(),
        {},
      ),
    onMutate: async () => {
      await cancelNotificationQueries(qc);
      const snapshots = getNotificationListSnapshots(qc);
      const previousUnreadCount = useNotificationStore.getState().unreadCount;
      const readAt = new Date().toISOString();

      patchNotificationLists(qc, (item, filters) => {
        if (item.archivedAt !== null || item.readAt !== null) return item;
        const next: StaffNotification = {
          ...item,
          readAt,
          seenAt: item.seenAt ?? readAt,
        };
        return matchesListFilters(next, filters) ? next : null;
      });
      setUnreadCount(0);
      return { snapshots, previousUnreadCount };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        restoreNotificationListSnapshots(qc, context.snapshots);
        setUnreadCount(context.previousUnreadCount);
      }
      toast.error("Couldn't mark all notifications as read");
    },
    onSettled: async () => {
      await invalidateNotificationQueries(qc);
    },
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: settingsKeys.notificationPreferences(),
    queryFn: () =>
      apiGet<NotificationPreferencesResponse>(
        operationsClient,
        settingsEndpoints.notificationPreferences(),
      ),
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateNotificationPreferencesDto) =>
      apiPatch<NotificationPreferencesResponse, UpdateNotificationPreferencesDto>(
        operationsClient,
        settingsEndpoints.notificationPreferences(),
        payload,
      ),
    onSuccess: (data) => {
      qc.setQueryData(settingsKeys.notificationPreferences(), data);
    },
  });
}

/**
 * Wipes the user's saved overrides so the server-side recommended defaults take
 * over again — the escape hatch for anyone whose earlier Save pinned every event
 * to every channel.
 */
export function useResetNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<NotificationPreferencesResponse, Record<string, never>>(
        operationsClient,
        settingsEndpoints.resetNotificationPreferences(),
        {},
      ),
    onSuccess: (data) => {
      qc.setQueryData(settingsKeys.notificationPreferences(), data);
    },
  });
}

export function useOperationalNotificationRules(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: settingsKeys.operationalNotificationRules(),
    queryFn: () =>
      apiGet<OperationalNotificationRules>(
        operationsClient,
        settingsEndpoints.operationalNotificationRules(),
      ),
    enabled: options.enabled ?? true,
  });
}

export function useUpdateOperationalNotificationRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateOperationalNotificationRulesDto) =>
      apiPatch<OperationalNotificationRules, UpdateOperationalNotificationRulesDto>(
        operationsClient,
        settingsEndpoints.operationalNotificationRules(),
        payload,
      ),
    onSuccess: (data) => {
      qc.setQueryData(settingsKeys.operationalNotificationRules(), data);
    },
  });
}

export function useCreateNotificationBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateNotificationBroadcastDto) =>
      apiPost<CreateNotificationBroadcastResponse, CreateNotificationBroadcastDto>(
        operationsClient,
        notificationEndpoints.broadcast(),
        payload,
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: notificationKeys.all() });
    },
  });
}

async function cancelNotificationQueries(qc: QueryClient): Promise<void> {
  await Promise.all([
    qc.cancelQueries({ queryKey: notificationKeys.lists() }),
    qc.cancelQueries({ queryKey: notificationKeys.unreadCount() }),
  ]);
}

async function invalidateNotificationQueries(qc: QueryClient): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: notificationKeys.lists() }),
    qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() }),
  ]);
}

function getNotificationListSnapshots(qc: QueryClient): NotificationListSnapshot {
  return qc.getQueriesData<NotificationsListCache>({
    queryKey: notificationKeys.lists(),
  });
}

function restoreNotificationListSnapshots(
  qc: QueryClient,
  snapshots: NotificationListSnapshot,
): void {
  for (const [queryKey, data] of snapshots) {
    qc.setQueryData(queryKey, data);
  }
}

function patchNotificationLists(
  qc: QueryClient,
  patcher: (item: StaffNotification, filters: NotificationListFilters) => StaffNotification | null,
): void {
  const queries = qc.getQueriesData<NotificationsListCache>({
    queryKey: notificationKeys.lists(),
  });

  for (const [queryKey, data] of queries) {
    if (!data) continue;
    const filters = readListFilters(queryKey);
    let changed = false;
    const pages = data.pages.map((page) => {
      const items: StaffNotification[] = [];
      for (const item of page.items) {
        const next = patcher(item, filters);
        if (next !== item) changed = true;
        if (next) items.push(next);
      }
      return changed ? { ...page, items } : page;
    });
    if (changed) qc.setQueryData(queryKey, { ...data, pages });
  }
}

function readListFilters(queryKey: QueryKey): NotificationListFilters {
  const maybeFilters = queryKey[queryKey.length - 1];
  if (!maybeFilters || typeof maybeFilters !== 'object' || Array.isArray(maybeFilters)) {
    return {};
  }
  return maybeFilters as NotificationListFilters;
}

function matchesListFilters(item: StaffNotification, filters: NotificationListFilters): boolean {
  if (filters.category && filters.category !== 'all' && item.category !== filters.category) {
    return false;
  }
  if (filters.priority && filters.priority !== 'all' && item.priority !== filters.priority) {
    return false;
  }
  if (filters.eventType && item.type !== filters.eventType) return false;
  if (filters.excludeEventType && item.type === filters.excludeEventType) return false;

  const status = filters.status ?? (filters.unreadOnly ? 'unread' : 'all');
  const archived = item.archivedAt !== null;
  const unread = item.readAt === null && !archived;
  switch (status) {
    case 'all':
      return !archived;
    case 'unread':
      return unread;
    case 'read':
      return item.readAt !== null && !archived;
    case 'archived':
      return archived;
  }
}

function matchesNotificationId(item: StaffNotification, id: string): boolean {
  return item.recipientId === id || item.id === id;
}
